import React, { useState, useEffect } from 'react';

// Get API base URL (same as api.js)
const getApiBaseUrl = () => {
  return process.env.REACT_APP_API_URL || 'https://api.getdecisionos.com';
};

const API_BASE_URL = getApiBaseUrl();

function FeedbackWidget({ testSlackId, setToast, isHidden = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState('Bug');
  const [severity, setSeverity] = useState('Blocker');
  const [message, setMessage] = useState('');
  const [includeUrl, setIncludeUrl] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen]);
  
  // Don't render if hidden (e.g., during onboarding tour)
  if (isHidden) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!message.trim()) {
      setToast('Please enter a message');
      setTimeout(() => setToast(null), 3000);
      return;
    }
    
    if (message.length > 1000) {
      setToast('Message too long (max 1000 characters)');
      setTimeout(() => setToast(null), 3000);
      return;
    }

    setIsSubmitting(true);

    try {
      const pageUrl = includeUrl ? window.location.href : null;
      
      const response = await fetch(`${API_BASE_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(process.env.NODE_ENV === 'development' && testSlackId ? { 'X-Slack-Id': testSlackId } : {})
        },
        credentials: 'include',
        body: JSON.stringify({
          type,
          severity,
          message: message.trim(),
          pageUrl
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      // Success!
      setToast("Feedback sent. Thanks for helping improve DecisionOS.");
      setTimeout(() => setToast(null), 4000);
      
      // Reset and close
      setMessage('');
      setType('Bug');
      setSeverity('Blocker');
      setIncludeUrl(true);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      setToast("Couldn't send - try again.");
      setTimeout(() => setToast(null), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    // Reset form
    setMessage('');
    setType('Bug');
    setSeverity('Blocker');
    setIncludeUrl(true);
  };

  return (
    <>
      {/* Floating Button */}
      <button
        className="feedback-widget-button"
        onClick={() => setIsOpen(true)}
        title="Report an issue"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '6px' }}>
          <path d="M8 1C4.13 1 1 4.13 1 8s3.13 7 7 7 7-3.13 7-7-3.13-7-7-7zm0 13c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z" fill="currentColor"/>
          <circle cx="8" cy="10.5" r="0.75" fill="currentColor"/>
          <path d="M8 4v4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        Report
      </button>

      {/* Modal */}
      {isOpen && (
        <div 
          className="feedback-widget-overlay"
          onClick={(e) => {
            if (e.target.className === 'feedback-widget-overlay') {
              setIsOpen(false);
            }
          }}
        >
          <div className="feedback-widget-modal">
            <div className="feedback-widget-header">
              <h3>Report an Issue</h3>
              <button 
                className="feedback-widget-close"
                onClick={() => setIsOpen(false)}
                title="Close"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="feedback-widget-form">
              {/* Type */}
              <div className="feedback-widget-field">
                <label>Type</label>
                <select 
                  value={type} 
                  onChange={(e) => setType(e.target.value)}
                  required
                >
                  <option value="Bug">Bug</option>
                  <option value="Issue">Issue</option>
                  <option value="Idea">Idea</option>
                  <option value="Question">Question</option>
                </select>
              </div>

              {/* Severity */}
              <div className="feedback-widget-field">
                <label>Severity</label>
                <select 
                  value={severity} 
                  onChange={(e) => setSeverity(e.target.value)}
                  required
                >
                  <option value="Blocker">Blocker</option>
                  <option value="Annoying">Annoying</option>
                  <option value="Nice-to-have">Nice-to-have</option>
                </select>
              </div>

              {/* Message */}
              <div className="feedback-widget-field">
                <label>
                  Message <span style={{ color: 'var(--error-red, #ef4444)' }}>*</span>
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe the issue or idea..."
                  rows={4}
                  maxLength={1000}
                  required
                />
                <div style={{ 
                  fontSize: '12px', 
                  color: 'var(--ink-muted, #6b7280)', 
                  marginTop: '4px',
                  textAlign: 'right'
                }}>
                  {message.length}/1000
                </div>
              </div>

              {/* Include URL */}
              <div className="feedback-widget-checkbox">
                <input
                  type="checkbox"
                  id="includeUrl"
                  checked={includeUrl}
                  onChange={(e) => setIncludeUrl(e.target.checked)}
                />
                <label htmlFor="includeUrl">Include page URL</label>
              </div>

              {/* Actions */}
              <div className="feedback-widget-actions">
                <button 
                  type="button"
                  onClick={handleCancel}
                  className="feedback-widget-btn-cancel"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="feedback-widget-btn-submit"
                  disabled={isSubmitting || !message.trim()}
                >
                  {isSubmitting ? 'Sending...' : 'Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default FeedbackWidget;

